import { StoreStatus } from 'common/utils/types'
import React, { type ReactElement, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { Skeleton, TablePagination, Typography, useTheme } from '@mui/material'
import { useAppDispatch, useAppSelector } from 'store/hooks'
import tendersBackground from 'assets/tendersBackground.svg'
import SimpleTooltip from 'common/ui/simple-tooltip'
import IconButton from 'common/ui/icon-button'
import { ReactComponent as RightArrowIcon } from 'assets/rightArrowIcon.svg'
import { type SxProps } from '@mui/system'
import { type ProposalStatus, type TenderStatus } from 'common/tenders/tenders.types'
import { BUTTON_SIZE, BUTTON_VARIANTS } from 'common/ui/theme-button/ThemeButton'
import { formatDate, getProposalStatus } from 'utils/functions'
import { getMyOffers } from 'features/vendor/vendor.service'

import styles from './MyOffers.module.scss'

export enum ProposalDetailsFromRoute {
  MY_OFFERS = 'my-offers',
}

const MY_OFFERS_PER_PAGE = 10
interface CellWrapperProps {
  title: string
  value?: string
  valueSx?: SxProps
  tenderStatus?: TenderStatus
  proposalStatus?: ProposalStatus
  deadlineProposal?: Date
  className?: string
  style: React.CSSProperties
  disableTooltip?: boolean
}

const TooltipCellWrapper: React.FC<CellWrapperProps> = ({
  title,
  value,
  valueSx,
  tenderStatus,
  proposalStatus,
  deadlineProposal,
  className,
  style,
  disableTooltip = false,
}): ReactElement => {
  const { palette } = useTheme()

  return (
    <div className={styles.cellInfoWrapper}>
      <div className={styles.info}>
        <Typography variant="subtitle1" sx={{ color: palette.gray.main }}>
          {title}
        </Typography>
        <div className={styles.tooltipWrapper}>
          <SimpleTooltip
            disableHoverListener={disableTooltip}
            title={value ?? ''}
            style={{ textAlign: 'left' }}
          >
            <Typography
              variant="title3"
              sx={valueSx}
              className={(tenderStatus && proposalStatus && className && styles[className]) ?? styles.value}
              style={style}
            >
              {value}
            </Typography>
          </SimpleTooltip>
        </div>
      </div>
    </div>
  )
}

const MyOffers: React.FC = (): ReactElement => {
  const { palette } = useTheme()
  const navigate = useNavigate()

  const dispatch = useAppDispatch()

  const { myOffersList, myOffersCount, myOffersPage, getMyOffersStatus } = useAppSelector(
    (state) => state.vendor,
  )

  const loadOffers = (page: number): void => {
    const payload = {
      ipp: MY_OFFERS_PER_PAGE,
      page,
    }

    void dispatch(getMyOffers(payload))
  }

  useEffect(() => {
    loadOffers(myOffersPage)
  }, [])

  const handlePageChange = (event: React.MouseEvent | null, page: number): void => {
    loadOffers(page)
  }

  const handleDetailsClick = (tenderId: string): void => {
    navigate(`/ponuda/${tenderId}`, { state: { source: ProposalDetailsFromRoute.MY_OFFERS } })
  }

  return (
    <div className={styles.container}>
      <div
        className={styles.backgroundImage}
        style={{
          backgroundImage: `url(${tendersBackground})`,
        }}
      />
      <div id="myOffersListWrapper" className={styles.myOffersListWrapper}>
        {getMyOffersStatus === StoreStatus.Succeeded && !myOffersList?.length && (
          <div className={styles.noResults}>Nema rezulata za trenutnu pretragu</div>
        )}
        {getMyOffersStatus !== StoreStatus.Loading && getMyOffersStatus !== StoreStatus.Idle ? (
          <div>
            {myOffersList.length !== 0 && (
              <div>
                <div className={styles.titleWrapper}>
                  <Typography variant="title1" className={styles.title}>
                    Detaljni opis tendera
                  </Typography>
                </div>
                <div className={styles.offerContainer}>
                  {myOffersList?.map((offer) => {
                    const { status, className } = getProposalStatus(
                      offer.tender.status,
                      offer.proposalStatus,
                      offer.deadlineProposal,
                    )
                    return (
                      <div key={offer.tender.id} className={styles.offer}>
                        <TooltipCellWrapper
                          title="Projekat"
                          value={offer.project.name ?? '—'}
                          style={{ minWidth: '300px', maxWidth: '300px' }}
                        />
                        <div className={styles.divider} />
                        <TooltipCellWrapper
                          title="Tender"
                          value={offer.tender.name ?? '—'}
                          style={{ minWidth: '300px', maxWidth: '300px' }}
                        />
                        <div className={styles.divider} />
                        <TooltipCellWrapper
                          title="Datum završetka tendera"
                          value={offer.deadlineResolution ? formatDate(offer.deadlineResolution) : '—'}
                          disableTooltip={true}
                          style={{ minWidth: '210px', maxWidth: '210px' }}
                        />
                        <div className={styles.divider} />
                        <TooltipCellWrapper
                          title="Status"
                          value={offer.tender.status ? status : '—'}
                          disableTooltip={true}
                          style={{ minWidth: '140px', maxWidth: '140px' }}
                          tenderStatus={offer.tender.status}
                          proposalStatus={offer.proposalStatus}
                          deadlineProposal={offer.deadlineProposal}
                          className={className}
                        />
                        <div className={styles.divider} />
                        <div
                          className={styles.buttonWrapper}
                          style={{ maxWidth: '90px', minWidth: '90px' }}
                          onClick={() => {
                            handleDetailsClick(offer?.tender?.id)
                          }}
                        >
                          <Typography variant="subtitle1" sx={{ color: palette.gray.main }}>
                            Detalji
                          </Typography>
                          <IconButton
                            variant={BUTTON_VARIANTS.outlined}
                            size={BUTTON_SIZE.small}
                            sx={{
                              borderColor: palette.blue600.main,
                              backgroundColor: palette.blue600.main,
                              width: '20px',
                              minWidth: '20px',
                              height: '20px',
                              padding: 0,
                            }}
                            onClick={() => {}}
                            icon={<RightArrowIcon style={{ stroke: 'white' }} />}
                          />
                        </div>
                      </div>
                    )
                  })}
                </div>
              </div>
            )}
          </div>
        ) : (
          Array(MY_OFFERS_PER_PAGE)
            .fill(0)
            .map((value, index) => (
              <Skeleton
                key={`skeleton-${index}`}
                variant="rounded"
                width={'100%'}
                height={96}
                sx={{ backgroundColor: palette.blue700.main, marginRight: '1rem' }}
              />
            ))
        )}
        <div className={styles.paginationWrapper}>
          {myOffersList?.length > 0 && (
            <TablePagination
              count={myOffersCount}
              onPageChange={handlePageChange}
              page={myOffersPage}
              rowsPerPage={MY_OFFERS_PER_PAGE}
              rowsPerPageOptions={[MY_OFFERS_PER_PAGE]}
              component="div"
            />
          )}
        </div>
      </div>
    </div>
  )
}

export default MyOffers
